import { useState, useRef, useEffect, useCallback } from "react";
import { Box, Paper, Typography, IconButton, TextField, Fab, Grow, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useLocation } from "react-router-dom";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { helpChatApi } from "./api";
import type { ChatTurn, ChatCitation } from "./types";
import CitationChip from "./components/CitationChip";
import MarkdownMessage from "./components/MarkdownMessage";
import { fetchOpenTickets, buildTicketPrompt, type OpenTicket } from "./tickets";

// ── Ask Aeronet palette (see docs/plans/help-docs-overhaul/ask-aeronet-widget-handoff.md) ──
const C = {
  teal: "#0a9ca2",
  tealHover: "#08868b",
  tealTint: "#e7f4f5",
  ink: "#172127",
  body: "#2a343b",
  muted: "#6b757c",
  faint: "#9aa3a9",
  border: "#e6eaec",
  border2: "#d7dde0",
  divider: "#eef1f2",
};
const FONT = '"Roboto", "Helvetica", "Arial", sans-serif';
const SOURCES_VISIBLE = 5;

const TICKET_ROUTE_RE = /^\/ticket-portal(\/|$)/;

const SUGGESTIONS = [
  "How do I create a job?",
  "How do I add a new pilot?",
  "What changed in the latest release?",
];

// Mounted once in AppLayout so the conversation survives navigation between
// modules; the button is shown only on the help-menu routes the widget has
// context for (What's New, Aeronet Help, My Tickets, Submit a Ticket), plus
// their legacy redirect aliases. Path is basename-relative (no "/app" prefix).
// Deliberately excludes /hub (and its aliases help-v2, aeronet-sops) and
// /feature-requests — the widget can't search those.
const HELP_ROUTE_RE =
  /^\/(aeronet-help|aeronet-release-notes|aeronet-roadmap|help-chat|help-center|help|release-notes|ticket-portal)(\/|$)/;

const STORAGE_KEY = "aeronet-help-chat:turns";

// Off by default: the widget renders only when the customer's `global` config
// sets `enable_help_chat` truthy. Mirrors isV2StationeryPreviewEnabled — an
// unset key returns false, so no backend change is needed to keep it dormant.
function isHelpChatEnabled(globalConfig?: { [key: string]: unknown }): boolean {
  const value = globalConfig?.enable_help_chat;
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    return normalized === "yes" || normalized === "true";
  }
  return value === true;
}

function loadTurns(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ── Inline icons (spec §8) ──────────────────────────────────────────────────
const Sparkle: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden fill={color} style={{ flexShrink: 0 }}>
    <path d="M12 2l1.9 5.6a3 3 0 0 0 1.9 1.9L21.5 11l-5.8 1.5a3 3 0 0 0-1.9 1.9L12 20l-1.8-5.6a3 3 0 0 0-1.9-1.9L2.5 11l5.8-1.5a3 3 0 0 0 1.9-1.9z" />
  </svg>
);
const PaperPlane: React.FC = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden fill="#fff">
    <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10l11 2-11 2z" />
  </svg>
);

// ── Sources + feedback block for an assistant turn ──────────────────────────
const AssistantExtras: React.FC<{ text: string; citations: ChatCitation[] }> = ({ text, citations }) => {
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);
  const [vote, setVote] = useState<"up" | "down" | null>(null);

  const shown = showAll ? citations : citations.slice(0, SOURCES_VISIBLE);
  const extra = citations.length - shown.length;

  const copy = () => {
    navigator.clipboard?.writeText(text).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => {}
    );
  };

  return (
    <>
      {citations.length > 0 && (
        <Box sx={{ mt: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "8px" }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".12em", color: C.faint }}>
              SOURCES
            </Typography>
            <Box sx={{ fontSize: "11px", fontWeight: 700, color: C.teal, bgcolor: C.tealTint, borderRadius: "999px", px: "7px", py: "1px" }}>
              {citations.length}
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {shown.map((c) => (
              <CitationChip key={`${c.space}-${c.page_id}`} citation={c} />
            ))}
            {extra > 0 && (
              <Box
                component="button"
                onClick={() => setShowAll(true)}
                sx={{
                  cursor: "pointer", fontSize: "13px", fontWeight: 600, color: C.muted,
                  bgcolor: "#fff", border: `1px solid ${C.border2}`, borderRadius: "10px",
                  px: "12px", py: "8px", transition: "all .15s ease",
                  "&:hover": { borderColor: C.teal, color: C.teal },
                }}
              >
                +{extra} more
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Feedback row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "16px", color: "#aab2b8" }}>
        <IconButton size="small" aria-label="Helpful" onClick={() => setVote(vote === "up" ? null : "up")}
          sx={{ p: "2px", color: vote === "up" ? C.teal : "inherit" }}>
          <ThumbUpAltOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" aria-label="Not helpful" onClick={() => setVote(vote === "down" ? null : "down")}
          sx={{ p: "2px", color: vote === "down" ? C.teal : "inherit" }}>
          <ThumbDownAltOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Box sx={{ width: "1px", height: "16px", bgcolor: C.divider }} />
        <IconButton size="small" aria-label="Copy answer" onClick={copy} sx={{ p: "2px", color: copied ? C.teal : "inherit" }}>
          {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
    </>
  );
};

/**
 * Floating "Ask Aeronet" help assistant. Mounted once at the app layout so it
 * persists across navigation; the launcher (bottom-right) opens an inline chat
 * that answers from the Aeronet documentation (via /v6/chat) with deep-linked
 * source chips. Conversation persists to localStorage. Visible only on help
 * routes. Styled to the Ask-Aeronet spec (Option A).
 */
const HelpChatWidget: React.FC = () => {
  const { pathname } = useLocation();
  const globalConfig = useGlobalConfig();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>(loadTurns);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [openTickets, setOpenTickets] = useState<OpenTicket[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastUserRef = useRef<HTMLDivElement>(null);

  const onTicketRoute = TICKET_ROUTE_RE.test(pathname);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(turns));
    } catch {
      /* ignore quota/serialisation errors */
    }
  }, [turns]);

  // Anchor the newest question to the top of the panel when it's sent, so the
  // answer renders below it and reads top-down. Deliberately NOT keyed on the
  // assistant reply or loading — the view must stay put while the answer fills
  // in; the user scrolls down at their own pace.
  const userTurnCount = turns.reduce((n, t) => n + (t.role === "user" ? 1 : 0), 0);
  useEffect(() => {
    lastUserRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [userTurnCount]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // On the My Tickets page, offer "Help me answer {key}" starter prompts.
  useEffect(() => {
    if (!open || !onTicketRoute) return;
    let cancelled = false;
    fetchOpenTickets(3)
      .then((t) => !cancelled && setOpenTickets(t))
      .catch(() => !cancelled && setOpenTickets([]));
    return () => {
      cancelled = true;
    };
  }, [open, onTicketRoute]);

  const runTurn = useCallback(
    async (displayText: string, buildPayload: () => Promise<string>) => {
      if (loading) return;
      setInput("");
      setTurns((t) => [...t, { role: "user", text: displayText }]);
      setLoading(true);
      try {
        const payload = await buildPayload();
        const res = await helpChatApi.postChat(payload);
        setTurns((t) => [
          ...t,
          {
            role: "assistant",
            text:
              res.answer ||
              "I couldn't find anything in your Aeronet documentation to answer that.",
            citations: res.grounded ? res.citations : [],
          },
        ]);
      } catch {
        setTurns((t) => [
          ...t,
          { role: "assistant", text: "Sorry — something went wrong. Please try again." },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  const ask = useCallback(
    (question: string) => {
      const q = question.trim();
      if (!q) return;
      void runTurn(q, async () => q);
    },
    [runTurn]
  );

  const askTicket = useCallback(
    (ticket: OpenTicket) => {
      void runTurn(`Help me answer ${ticket.key}`, () => buildTicketPrompt(ticket.key));
    },
    [runTurn]
  );

  // Gated off unless the customer's global config opts in, and — even then —
  // rendered only inside the help menu (state persists across navigation).
  if (!isHelpChatEnabled(globalConfig)) return null;
  if (!HELP_ROUTE_RE.test(pathname)) return null;

  const isEmpty = turns.length === 0;
  let lastUserIdx = -1;
  turns.forEach((t, i) => { if (t.role === "user") lastUserIdx = i; });
  const newChat = () => {
    setTurns([]);
    setInput("");
    inputRef.current?.focus();
  };

  const ticketMode = onTicketRoute && openTickets.length > 0;
  const starterPrompts: { label: string; onClick: () => void }[] = ticketMode
    ? openTickets.map((t) => ({ label: `Help me answer ${t.key}`, onClick: () => askTicket(t) }))
    : SUGGESTIONS.map((s) => ({ label: s, onClick: () => ask(s) }));

  const iconBtn = {
    width: 30, height: 30, color: "rgba(255,255,255,.85)",
    "&:hover": { bgcolor: "rgba(255,255,255,.15)" },
    "&.Mui-disabled": { color: "rgba(255,255,255,.4)" },
  };

  return (
    <>
      <Grow in={open} style={{ transformOrigin: "bottom right" }}>
        <Paper
          role="dialog"
          aria-label="Ask Aeronet help assistant"
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 96 },
            right: { xs: 16, sm: 24 },
            width: { xs: "calc(100vw - 32px)", sm: 440 },
            height: { xs: "calc(100vh - 32px)", sm: 660 },
            maxHeight: { sm: "calc(100vh - 120px)" },
            display: open ? "flex" : "none",
            flexDirection: "column",
            borderRadius: "16px",
            overflow: "hidden",
            textAlign: "left",
            fontFamily: FONT,
            bgcolor: "#fff",
            boxShadow: "0 16px 44px -12px rgba(12,40,44,.28), 0 0 0 1px rgba(12,40,44,.05)",
            zIndex: 1301,
          }}
        >
          {/* Header */}
          <Box sx={{ px: "18px", py: "16px", bgcolor: C.teal, display: "flex", alignItems: "center", gap: "12px" }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sparkle size={20} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: "19px", fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
                Ask Aeronet
              </Typography>
            </Box>
            <IconButton onClick={newChat} disabled={isEmpty} aria-label="New chat" title="New chat" sx={iconBtn}>
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton onClick={() => setOpen(false)} aria-label="Close help assistant" sx={iconBtn}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Body */}
          <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: "18px", py: "18px", bgcolor: "#fff", display: "flex", flexDirection: "column", gap: "14px" }}>
            {isEmpty && (
              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: "21px", fontWeight: 700, color: C.ink, letterSpacing: "-.02em" }}>
                  How can I help?
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: "14.5px", color: C.muted, mt: "2px", mb: "16px" }}>
                  {ticketMode ? "Pick a ticket and I'll try to answer it from your docs." : "Ask a question about using Aeronet."}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {starterPrompts.map((p) => (
                    <Box
                      key={p.label}
                      component="button"
                      onClick={p.onClick}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
                        width: "100%", textAlign: "left", cursor: "pointer",
                        fontFamily: FONT, fontSize: "14.5px", fontWeight: 600, color: C.muted,
                        border: `1px solid ${C.border}`, borderRadius: "12px", px: "16px", py: "14px",
                        bgcolor: "#fff", transition: "all .15s ease",
                        "&:hover": { borderColor: C.teal, bgcolor: "#f5fbfb" },
                      }}
                    >
                      <span>{p.label}</span>
                      <ChevronRightIcon sx={{ fontSize: 20, color: C.teal, flexShrink: 0 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {turns.map((turn, i) => {
              const isUser = turn.role === "user";
              if (isUser) {
                return (
                  <Box key={i} ref={i === lastUserIdx ? lastUserRef : undefined} sx={{ alignSelf: "flex-end", maxWidth: "80%", bgcolor: C.teal, color: "#fff", borderRadius: "14px 14px 4px 14px", px: "14px", py: "10px" }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: "14.5px", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                      {turn.text}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box key={i} sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: C.tealTint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "2px" }}>
                    <Sparkle size={17} color={C.teal} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: "14.5px", lineHeight: 1.65, color: C.body }}>
                    <MarkdownMessage text={turn.text} />
                    <AssistantExtras text={turn.text} citations={turn.citations ?? []} />
                  </Box>
                </Box>
              );
            })}

            {loading && (
              <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: C.tealTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Sparkle size={17} color={C.teal} />
                </Box>
                <Box sx={{ display: "flex", gap: "4px" }}>
                  {[0, 1, 2].map((d) => (
                    <Box key={d} sx={{
                      width: 7, height: 7, borderRadius: "50%", bgcolor: C.faint,
                      animation: "askdot 1s infinite ease-in-out", animationDelay: `${d * 0.15}s`,
                      "@keyframes askdot": { "0%,80%,100%": { opacity: 0.3 }, "40%": { opacity: 1 } },
                    }} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Input footer */}
          <Box sx={{ borderTop: `1px solid ${C.divider}`, px: "18px", pt: "10px", pb: "12px", bgcolor: "#fff" }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: "8px",
              border: `1.5px solid ${C.border2}`, borderRadius: "12px", pl: "16px", pr: "5px", py: "4px",
              transition: "border-color .15s ease", "&:focus-within": { borderColor: C.teal },
            }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                variant="standard"
                value={input}
                disabled={loading}
                placeholder={isEmpty ? "Ask a question…" : "Ask a follow-up…"}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    ask(input);
                  }
                }}
                InputProps={{ disableUnderline: true }}
                sx={{
                  // Kill MUI's asymmetric multiline padding (4px top / 5px bottom)
                  // on every level so the text sits dead-centre in the row.
                  "& .MuiInputBase-root, & .MuiInputBase-multiline, & .MuiInput-root": { p: 0, m: 0 },
                  "& textarea": {
                    fontFamily: FONT, fontSize: "14.5px", lineHeight: 1.5, color: C.body, p: 0, m: 0,
                    "&::placeholder": { fontFamily: FONT, color: C.muted, opacity: 1 },
                  },
                }}
              />
              <IconButton
                aria-label="Send"
                disabled={loading || !input.trim()}
                onClick={() => ask(input)}
                sx={{
                  width: 34, height: 34, borderRadius: "10px", bgcolor: C.teal, flexShrink: 0,
                  boxShadow: "0 4px 10px rgba(10,156,162,.32)",
                  "&:hover": { bgcolor: C.tealHover },
                  "&.Mui-disabled": { bgcolor: C.border2, boxShadow: "none" },
                }}
              >
                <PaperPlane />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Grow>

      {/* Launcher */}
      <Fade in={!open}>
        <Fab
          aria-label="Open Ask Aeronet"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            bgcolor: C.teal,
            "&:hover": { bgcolor: C.tealHover },
            boxShadow: "0 8px 20px -4px rgba(10,156,162,.5)",
            zIndex: 1300,
          }}
        >
          <Sparkle size={24} />
        </Fab>
      </Fade>
    </>
  );
};

export default HelpChatWidget;
